(ns client-portal.client.ai-agent.views
  "Full-page agent: the agent picker landing/loading states, the report-builder view
   (which renders a generated report, hence the dependency on client.reports), the
   in-chat component renderer registered for chat.views, and the page `root`. The
   conversation UI itself lives in client.ai-agent.chat.views, which is report-free so
   it can also power the report-chat side panel."
  (:require [client-portal.client.ai-agent.chat.data :as chat.d]
            [client-portal.client.anonymous.data :as anon.d]
            [client-portal.client.ai-agent.chat.views :as chat.views]
            [client-portal.client.ai-agent.data :as data]
            [client-portal.client.ai-agent.history.views]
            [client-portal.client.ai-agent.report-history.views :as report-history.views]
            [client-portal.client.voice-insights.empty-state :as empty-state]
            [clojure.string :as s]
            [shared.core.amelia-light.components.components :as alc]
            [client-portal.client.reports.individual.data :as reports.d]
            [client-portal.client.reports.individual.views :as reports.individual]
            [client-portal.client.layout :as l]
            [shared.core.reactive :as re]
            [shared.core.style :as style]
            [shared.core.time :as time]
            [shared.core.util :as util]))

(defn- share-section
  "Who the report is shared with, and the controls to change it. Rendered only once the
   report has been saved — a direct-by-email share is written the moment it is submitted,
   so there has to be a report to write it against. Organization selections instead ride
   in the settings form and are written by the dialog's Save button. Reaching the builder
   at all already required the right to edit the report, so everyone here can share it."
  []
  (let [shares- (re/subscribe [:ai-agent/report-shares-])
        share-users- (re/subscribe [:ai-agent/report-share-users-])
        sharing?- (re/subscribe [:ai-agent/sharing-report?-])
        email- (re/subscribe [:form/val- data/share-form-path :email-address])
        organizations- (re/subscribe [:ai-agent/share-organizations-])
        ;; The row whose remove has been clicked once and is waiting on a confirmation,
        ;; so a stray click does not drop someone's access outright.
        confirming-user-id* (re/ratom nil)]
    (fn []
      (let [valid-email? (util/valid-email-address? @email-)
            share! (fn []
                     (when (and valid-email? (not @sharing?-))
                       (re/dispatch [:ai-agent/share-report-with-user])))
            hint-style {:color (style/alc-color "Text/secondary")
                        :font-size 12
                        :line-height 1.33}]
        [:div {:style {:border-top (str "1px solid " (style/alc-color "Border/divider_secondary"))
                       :display :flex
                       :flex-direction :column
                       :gap 8
                       :margin-top 4
                       :padding-top 12}}
         [:span {:style {:color (style/alc-color "Text/primary_header")
                         :font-size 14
                         :font-weight 500}}
          "Share"]
         [:div {:style {:display :flex
                        :gap 8}}
          [alc/up-input {:form-path data/share-form-path
                         :form-name :email-address
                         :placeholder "Email address"
                         :class-name "flex-1"
                         :on-key-down (fn [e]
                                        (when (util/enter-key-event? e)
                                          (.preventDefault e)
                                          (share!)))}]
          [alc/up-button {:on-click share!
                          :disabled (not valid-email?)
                          :loading? @sharing?-}
           "Share"]]
         [:span {:style hint-style}
          "Any emails provided here will be provided with immediate access (including the ability to edit this report)."]
         (if (not (map? @shares-))
           [:span {:style hint-style} "Loading shares..."]
           ;; Always at least one row: the owner, who leads the list and has no remove
           ;; action — their access comes with the report, not from a share.
           (into [:div {:style {:display :flex
                                :flex-direction :column
                                :gap 4}}]
                 (for [{:keys [user-id email-address is-owner]} @share-users-]
                   ^{:key user-id}
                   [:div {:style {:align-items :center
                                  :display :flex
                                  :gap 8
                                  :justify-content :space-between
                                  :min-height 28}}
                    [:span {:style {:color (style/alc-color "Text/primary")
                                    :font-size 13
                                    :overflow :hidden
                                    :text-overflow :ellipsis
                                    :white-space :nowrap}}
                     email-address]
                    (cond
                      is-owner
                      [:span {:style {:color (style/alc-color "Text/secondary")
                                      :flex "0 0 auto"
                                      :font-size 12}}
                       "Owner"]

                      (= @confirming-user-id* user-id)
                      [:div {:style {:align-items :center
                                     :display :flex
                                     :flex "0 0 auto"
                                     :gap 4}}
                       [:span {:style {:color (style/alc-color "Text/secondary")
                                       :font-size 12
                                       :margin-right 8}}
                        "Remove?"]
                       [alc/up-button {:icon-type :DoneIcon
                                       :variant "destructive"
                                       :tooltip-content "Remove access"
                                       :on-click (fn []
                                                   (reset! confirming-user-id* nil)
                                                   (re/dispatch [:ai-agent/unshare-report-with-user user-id]))}]
                       [alc/up-button {:icon-type :CrossIcon
                                       :variant "ghost"
                                       :tooltip-content "Keep access"
                                       :on-click #(reset! confirming-user-id* nil)}]]

                      :else
                      [alc/up-button {:icon-type :DeleteIcon
                                      :variant "ghost"
                                      :tooltip-content "Remove access"
                                      :on-click #(reset! confirming-user-id* user-id)}])])))
         [alc/up-multiple-dropdown {:placeholder "Share with organizations"
                                    :form-path data/report-settings-form-path
                                    :form-name :tenant-organization-ids
                                    :options (mapv (fn [{:keys [id label]}]
                                                     {:text label
                                                      :value id})
                                                   (util/when-not-loading @organizations-))
                                    :style {:margin-top 10}}]
         [:span {:style hint-style}
          "Everyone in a selected organization can see this report."]]))))

(defn- filters-section
  "Which of the report's filter controls are shown to its viewers. Saved into the
   report configuration, which is what both the layout and the load params read."
  []
  [:div {:style {:display :flex
                 :flex-direction :column
                 :gap 8}}
   [:span {:style {:color (style/alc-color "Text/primary_header")
                   :font-size 14
                   :font-weight 500}}
    "Filters"]
   (into [:div {:style {:display :flex
                        :flex-direction :column
                        :gap 10}}]
         (for [{:keys [key label]} reports.d/report-filter-options]
           ^{:key key}
           [:label {:style {:align-items :center
                            :color (style/alc-color "Text/primary")
                            :cursor :pointer
                            :display :flex
                            :font-size 13
                            :gap 8}}
            [alc/up-checkbox {:form-path data/report-settings-form-path
                              :form-name key}]
            label]))
   [:span {:style {:color (style/alc-color "Text/secondary")
                   :font-size 12
                   :line-height 1.33}}
    "Unchecked filters are hidden from the report and are not applied to its data. Concept is always applied."]])

(defmethod alc/dialog-view
  :report/settings-dialog [_ _]
  (let [concepts (util/when-not-loading @(re/subscribe [:concepts/list-data-]))
        saved? (not-empty @(re/subscribe [:ai-agent/saved-report-public-id-]))
        ;; The live form title, so the header tracks a rename the user has typed but not
        ;; saved yet; `seeded-title` covers an untouched one.
        {:keys [title seeded-title]} @(re/subscribe [:ai-agent/report-form-])
        report-name (or (not-empty title) (not-empty seeded-title))
        ;; The dialog sizes to its widest child but never below 32rem, and its body pads
        ;; 16px a side — so this is exactly the content width of the narrowest it renders.
        ;; Pinning to it fills that width instead of leaving a gap, without widening it.
        mobile? @(re/subscribe [:core/mobile-layout?-])
        content-width (if mobile? "100%" 480)]
    [alc/dialog {;; Clamped with styles, not `line-clamp-2`: base-ui ships a fixed
                 ;; stylesheet that has no rule for that class.
                 :title [:span {:style (cond-> {:display "-webkit-box"
                                                :-webkit-box-orient "vertical"
                                                :-webkit-line-clamp 2
                                                :max-width content-width
                                                :overflow "hidden"}
                                               mobile? (assoc :font-size 20
                                                              :padding-right 24))}
                         (if report-name
                           (str "\"" report-name "\" Settings")
                           "Report Settings")]
                 :on-close #(re/dispatch [:ai-agent/cancel-report-settings])
                 :footer [:<>
                          [alc/up-call-to-action-button {:on-click #(if saved?
                                                                      (re/dispatch [:ai-agent/save-report-settings+shares])
                                                                      (re/dispatch [:ai-agent/apply-report-settings]))
                                                         :loading? (or @(re/subscribe [:ai-agent/saving-report-settings?-])
                                                                       @(re/subscribe [:ai-agent/saving-org-shares?-]))}
                           (if saved? "Save" "Update")]
                          [alc/up-button {:on-click #(re/dispatch [:ai-agent/cancel-report-settings])}
                           "Cancel"]]}
     [:div {:style {:display :flex
                    :flex-direction :column
                    :gap 8
                    :width content-width}}
      ;; Nothing to target with a single concept.
      (when (> (count concepts) 1)
        [:<>
         [alc/up-multiple-dropdown {:placeholder "All Concepts"
                                    :form-path data/report-settings-form-path
                                    :form-name :target-concept-ids
                                    :options (mapv (fn [{:keys [id] :as concept}]
                                                     {:text (anon.d/concept-label concept)
                                                      :value id})
                                                   concepts)}]
         [:span {:style {:color (style/alc-color "Text/secondary")
                         :font-size 12
                         :line-height 1.33}}
          (str "By default all concepts will receive the report (within organizations given access). If you want to limit which concepts the report is for, make those selections here."
               (when-not saved?
                 " They will be applied when the report is saved."))]])
      [:div {:style {:border-top (str "1px solid " (style/alc-color "Border/divider_secondary"))
                     :margin-top 4
                     :padding-top 12}}
       [filters-section]]
      (when saved?
        [share-section])]]))

(defmethod alc/dialog-view
  :report/save-conflict-dialog [_ _]
  (let [{:keys [last-modified-by-email-address last-modified-at]} @(re/subscribe [:ai-agent/save-conflict-])
        saving? @(re/subscribe [:ai-agent/saving-report?-])
        mobile? @(re/subscribe [:core/mobile-layout?-])
        secondary-style {:color (style/alc-color "Text/secondary")
                         :font-size 12
                         :line-height 1.33}]
    [alc/dialog {:title "This Report Has Changed"
                 :on-close #(re/dispatch [:ai-agent/close-save-conflict])
                 :footer [:<>
                          [alc/up-call-to-action-button {:on-click #(re/dispatch [:ai-agent/save-report :copy])
                                                         :loading? saving?}
                           "Save as New Report"]
                          [alc/up-button {:on-click #(re/dispatch [:ai-agent/save-report :overwrite])
                                          :disabled saving?}
                           "Overwrite"]
                          [alc/up-button {:on-click #(re/dispatch [:ai-agent/load-latest-report])
                                          :disabled saving?}
                           "Load Latest"]]}
     [:div {:style {:display :flex
                    :flex-direction :column
                    :gap 12
                    :width (if mobile? "100%" 480)}}
      [:span {:style {:color (style/alc-color "Text/primary")
                      :font-size 14
                      :line-height 1.43}}
       "A newer version of this report was saved after you started editing it."]
      (when (not-empty last-modified-by-email-address)
        [:span {:style secondary-style}
         (str "Last saved by " last-modified-by-email-address
              (when-let [ago (not-empty (time/relative-time-label last-modified-at))]
                (str " · " ago)))])
      [:span {:style secondary-style}
       "Overwriting replaces their changes with yours (theirs stay in the report's history). Loading the latest discards your unsaved changes and starts a new edit from their version."]]]))

(defn message-components
  "The report components an agent message answered with (the data-question agent),
   rendered inline under its markdown. `components` is the resolved grid as a JSON
   STRING — parsed to the raw string-keyed map the report grid renders, exactly like a
   loaded report's configuration. `report-id` is the message id, so each chart gets a
   stable id across re-renders."
  [{:keys [components report-id]}]
  (let [configuration (util/json-string->map-basic components)]
    (when (not-empty (get configuration "content"))
      [reports.individual/component-grid {:configuration configuration
                                          :view-only? true
                                          :report-id report-id}])))

(reset! chat.d/message-components-view* message-components)

(defn- show-chat-button []
  [:div {:style {:position :sticky
                 :top 0
                 :height 0
                 :z-index (+ 2 (style/z :medium))}}
   [:div {:style {:position :absolute
                  :top 10
                  :right 0}}
    [alc/tooltip
     {:target-content [alc/up-button {:icon-type :InteractionsIcon
                                      :variant "ghost"
                                      :on-click #(re/dispatch [:ai-agent/set-builder-chat-open true])}]
      :tooltip-content "Show Chat"}]]])

(defn- report-version-preview
  "A past saved version of the report, picked from the History view, rendered over the
   header's view in place of the builder's own report."
  [_]
  (let [report-id (gensym "report-version-")
        version-report- (re/subscribe [:ai-agent/report-version-report-])
        single-pane?- (re/subscribe [:layout/single-pane?-])
        in-flight?- (re/subscribe [:ai-agent/response-in-flight?-])
        newer-saved?- (re/subscribe [:ai-agent/newer-report-version-saved?-])]
    (fn [{:keys [version title description current?] :as entry}]
      (let [version-report @version-report-
            configuration (:configuration version-report)
            active-color (style/alc-color "Common/active")]
        [:div {:style {:flex 1
                       :display :flex
                       :flex-direction :column}}
         (when @single-pane?-
           [show-chat-button])
         [:div {:style {:background-color (style/alc-color "Background/primary")
                        :flex "0 0 auto"
                        :display :flex
                        :flex-direction :column
                        :gap 10
                        :padding (if @single-pane?- "10px 44px 10px 0" "10px 0")
                        :position :sticky
                        :top 0
                        :width "100%"
                        :z-index (inc (style/z :medium))}}
          [:div {:style {:align-items :center
                         :background-color (util/hex->rgba active-color 0.12)
                         :border (str "1px solid " (util/hex->rgba active-color 0.5))
                         :border-radius 8
                         :display :flex
                         :flex-wrap :wrap
                         :gap 10
                         :padding "10px 12px"}}
           [:div {:style {:align-items :center
                          :display :flex
                          :flex "1 1 240px"
                          :gap 10
                          :min-width 0}}
            [alc/up-icon {:icon-type :HistoryIcon
                          :style {:color active-color}}]
            [:div {:style {:display :flex
                           :flex-direction :column
                           :gap 2
                           :min-width 0}}
             [:span {:style {:color (style/alc-color "Text/primary_header")
                             :font-size 14
                             :font-weight 500
                             :line-height 1.43}}
              (str "Previewing Version " version (when current? " (Current)"))]
             [:span {:style {:color (style/alc-color "Text/secondary")
                             :font-size 12
                             :line-height 1.33}}
              (report-history.views/version-saved-label entry)]]]
           [:div {:style {:flex "0 0 auto"
                          :display :flex
                          :align-items :center
                          :gap 8}}
            [alc/up-button {:on-click #(re/dispatch [:ai-agent/select-report-version nil])}
             (if @newer-saved?- "Back to Editing" "Back to Current")]
            (if current?
              [alc/up-call-to-action-button {:on-click #(re/dispatch [:ai-agent/load-latest-report])
                                             :tooltip-content "Start a new edit from this version. Unsaved changes in the builder are discarded."}
               "Load Latest"]
              [alc/up-call-to-action-button {:on-click #(re/dispatch [:ai-agent/open-restore-report-version])
                                             :disabled @in-flight?-}
               "Restore Version"])]]
          [:div {:style {:display :flex
                         :flex-direction :column
                         :gap 4
                         :min-width 0}}
           [:span {:style {:color (style/alc-color "Text/primary_header")
                           :font-size 24
                           :font-weight 400
                           :line-height 1.33
                           :overflow-wrap :anywhere}}
            title]
           (when (not-empty description)
             [:span {:style {:color (style/alc-color "Text/secondary")
                             :font-size 18
                             :font-weight 400
                             :line-height 1.33
                             :overflow-wrap :anywhere}}
              description])]]
         [:div {:style {:flex 1
                        :display :flex
                        :flex-direction :column}}
          (cond
            (:error? version-report)
            [:div {:style {:display :flex
                           :flex-direction :column
                           :align-items :center
                           :gap 12
                           :padding "60px 0"}}
             [:span {:style {:color (style/alc-color "Text/primary")
                             :font-size 14}}
              (str "Failed to load version " version ".")]
             [alc/up-button {:on-click #(re/dispatch [:ai-agent/load-report-version :reload])}
              "Try Again"]]

            (not (map? version-report))
            [:div {:style {:flex 1
                           :display :flex}}
             [empty-state/loading-card {:label "Loading Version..."}]]

            :else
            [reports.individual/report-details (assoc version-report :view-only? true
                                                                     :report-id report-id
                                                                     :report-filters (reports.d/report-filters (get configuration "filters")))])]]))))

(defn report-builder []
  (let [report-id (gensym "report-builder-")
        saving?- (re/subscribe [:ai-agent/saving-report?-])
        saved-public-id- (re/subscribe [:ai-agent/saved-report-public-id-])
        report-form- (re/subscribe [:ai-agent/report-form-])
        session- (re/subscribe [:ai-agent/session-])
        query-params- (re/subscribe [:router/current-query-params-])
        reloading?- (re/subscribe [:ai-agent/session-reloading?-])
        current-concept-id- (re/subscribe [:concepts/current-concept-id-])
        target-concept-ids- (re/subscribe [:ai-agent/report-settings-target-concept-ids-])
        builder-filters- (re/subscribe [:ai-agent/builder-report-filters-])
        single-pane?- (re/subscribe [:layout/single-pane?-])
        in-flight?- (re/subscribe [:ai-agent/response-in-flight?-])
        previewed-version- (re/subscribe [:ai-agent/previewed-report-version-])
        newer-saved?- (re/subscribe [:ai-agent/newer-report-version-saved?-])
        report-held?- (re/subscribe [:ai-agent/builder-report-held?-])]
    (fn []
      (cond
        (util/loading? @session-)
        [:div {:style {:flex 1
                       :display :flex}}
         [empty-state/loading-card {:label "Loading Report..."}]]

        @previewed-version-
        [report-version-preview @previewed-version-]

        :else
        (let [{:keys [report saved-report] :as report-msg} (->> @(re/subscribe [:ai-agent/session-messages-])
                                                                (filter :report)
                                                                (last))
              component-flags (merge (zipmap (:added-component-ids report-msg) (repeat :added))
                                     (zipmap (:changed-component-ids report-msg) (repeat :updated)))]
          (when (and (:id report-msg) (not= (:id report-msg) (:message-id @report-form-)))
            (re/dispatch [:ai-agent/seed-report-form (cond-> {:session-id (:id @session-)
                                                              :message-id (:id report-msg)
                                                              :title (:title saved-report)
                                                              :title-changed (:title-changed report-msg)
                                                              :draft (:report-draft @session-)}
                                                             (contains? saved-report :description)
                                                             (assoc :description (:description saved-report)))]))
          [:div {:style {:flex 1
                         :display :flex
                         :flex-direction :column}}
           (when @single-pane?-
             [show-chat-button])
           (when (and (not-empty report) @newer-saved?-)
             [report-history.views/newer-version-banner])
           (when (not-empty report)
             [:div {:style {:background-color (style/alc-color "Background/primary")
                            :flex "0 0 auto"
                            :display :flex
                            :flex-wrap :wrap
                            :align-items :flex-start
                            :gap 8
                            :padding (if @single-pane?- "10px 44px 10px 0" "10px 0")
                            :position :sticky
                            :top 0
                            :width "100%"
                            :z-index (inc (style/z :medium))}}
              ;; The basis keeps the title readable — the buttons wrap below it rather
              ;; than squeezing it to nothing in a narrow pane.
              [:div {:style {:flex "1 1 240px"
                             :display :flex
                             :flex-direction :column
                             :gap 4
                             :min-width 0}}
               [alc/up-textarea {:form-path data/report-form-path
                                 :form-name :title
                                 :placeholder "Set Title"
                                 :on-blur #(re/dispatch [:ai-agent/save-report-draft])
                                 :rows 1
                                 :style {:background-color :transparent
                                         :border :none
                                         :box-shadow :none
                                         :color (style/alc-color "Text/primary_header")
                                         :field-sizing :content
                                         :font-size 24
                                         :font-weight 400
                                         :line-height 1.33
                                         :min-height :unset
                                         :outline :none
                                         :padding 0
                                         :resize :none
                                         :width "100%"}}]
               [alc/up-textarea {:form-path data/report-form-path
                                 :form-name :description
                                 :placeholder "Set Description"
                                 :on-blur #(re/dispatch [:ai-agent/save-report-draft])
                                 :rows 1
                                 :style {:background-color :transparent
                                         :border :none
                                         :box-shadow :none
                                         :color (style/alc-color "Text/secondary")
                                         :field-sizing :content
                                         :font-size 18
                                         :font-weight 400
                                         :line-height 1.33
                                         :min-height :unset
                                         :outline :none
                                         :padding 0
                                         :resize :none
                                         :width "100%"}}]]
              [:div {:style {:flex "0 0 auto"
                             :display :flex
                             :align-items :center
                             :gap 8}}
               [alc/up-button {:icon-type :SettingsIcon
                               :tooltip-content "Report Settings"
                               :on-click #(re/dispatch [:ai-agent/open-report-settings])}]
               (when (not-empty @saved-public-id-)
                 (let [target-concept-ids @target-concept-ids-
                       no-access? (and (not-empty target-concept-ids)
                                       (not (some #(= (str %) (str @current-concept-id-))
                                                  target-concept-ids)))]
                   [alc/up-button (cond-> {:on-click (fn [e]
                                                       (if (or (.-metaKey e) (.-ctrlKey e))
                                                         (re/dispatch [:router/go-open-new [:reports/individual
                                                                                            {:report-id @saved-public-id-}
                                                                                            @query-params-]])
                                                         (re/dispatch [:reports/open-report @saved-public-id- @query-params-])))
                                           :disabled no-access?}
                                          no-access?
                                          (assoc :tooltip-content "The selected concept does not have access to this report."))
                    "View Report"]))
               [alc/up-call-to-action-button {:on-click #(re/dispatch [:ai-agent/save-report])
                                              :disabled (empty? (:configuration saved-report))
                                              :loading? @saving?-}
                "Save Report"]]])
           [:div {:style {:flex 1
                          :display :flex
                          :flex-direction :column
                          :padding-top (if (not-empty component-flags) 12 0)}}
            (if @report-held?-
              [:div {:style {:flex 1
                             :display :flex}}
               [empty-state/loading-card {:label "Refreshing Report..."}]]
              (let [configuration (util/json-string->map-basic (:configuration report))]
                [reports.individual/report-details (assoc report :configuration configuration
                                                                 :view-only? true
                                                                 :report-id report-id
                                                                 :component-flags component-flags
                                                                 :report-filters (or @builder-filters-
                                                                                     (reports.d/report-filters (get configuration "filters"))))]))
            (let [status-label (cond
                                 (and @single-pane?- @in-flight?-) "Thinking..."
                                 (and @reloading?- (not @report-held?-)) "Report Refreshing")]
              (when status-label
                [:div {:style {:position :sticky
                               :bottom 14
                               :height 0
                               :display :flex
                               :justify-content :center
                               :z-index (inc (style/z :medium))}}
                 [:div {:style {:display :flex
                                :align-items :center
                                :background-color (style/alc-color "Background/primary")
                                :border (str "1px solid " (style/alc-color "Border/divider_secondary"))
                                :border-radius 16
                                :box-shadow (style/alc-shadow "shadow/common_overlay")
                                :padding 14
                                :transform "translateY(-100%)"}}
                  [alc/up-loader-with-label {:label status-label
                                             :label-style {:color (style/alc-color "Text/secondary")}
                                             :loader-size "xsmall"
                                             :style {:flex-direction :row}}]]]))]])))))

(defn root [_]
  (let [config- (re/subscribe [:voice-insights/concept-configuration-])
        session-id- (re/subscribe [:core/ai-agent-session-id-])
        active-panel-right- (re/subscribe [:layout/active-panel-right-])
        report-builder?- (re/subscribe [:ai-agent/report-builder-agent?-])
        selected-agent- (re/subscribe [:ai-agent/selected-agent-])
        agent-list- (re/subscribe [:core/ai-agent-list-])
        cur-route- (re/subscribe [:router/current-route-])
        rb-agent- (re/subscribe [:ai-agent/report-builder-agent-])
        session-started?- (re/subscribe [:ai-agent/session-started?-])
        history-collapsed?- (re/subscribe [:ai-agent/history-collapsed?-])
        url-session-id- (re/subscribe [:ai-agent/url-session-id-])
        mobile?- (re/subscribe [:core/mobile-layout?-])
        single-pane?- (re/subscribe [:layout/single-pane?-])
        builder-chat-open?- (re/subscribe [:ai-agent/builder-chat-open?-])
        builder-filters- (re/subscribe [:ai-agent/builder-report-filters-])
        builder-report-id- (re/subscribe [:ai-agent/builder-report-public-id-])
        builder-target-concept-ids- (re/subscribe [:ai-agent/builder-report-target-concept-ids-])
        ;; Held until the report's settings land: they decide which filter controls the
        ;; report shows, and the session load's own params depend on them.
        report-settings-ready? #(or (nil? @builder-report-id-)
                                    (some? @builder-filters-))
        report-builder-route? #(contains? chat.d/report-builder-routes (first @cur-route-))
        summary-route? #(contains? chat.d/summary-agent-routes (first @cur-route-))
        kind-agent-ready? #(cond
                             (report-builder-route?) (some? @rb-agent-)
                             (summary-route?) (some? @selected-agent-)
                             :else true)
        requested-session* (re/ratom nil)
        load-props-session! (fn [props]
                              (let [props-session-id (:session-id props)]
                                (if (s/blank? props-session-id)
                                  ;; Off the session route — forget what was requested so
                                  ;; coming back to that same session loads it again
                                  ;; (the conversation went with the route change).
                                  (reset! requested-session* nil)
                                  (when (and (not= props-session-id @requested-session*)
                                             (not= props-session-id @session-id-))
                                    (reset! requested-session* props-session-id)
                                    (re/dispatch [:ai-agent/load-session])))))
        sync-panel! (fn [props]
                      (let [panel (cond
                                    ;; With a session in the URL, hold the builder's chat
                                    ;; panel until that session has actually loaded (avoids
                                    ;; the panel fronting a still-loading report, e.g. when
                                    ;; opening a report edit).
                                    (and @report-builder?-
                                         (or (not @single-pane?-) @builder-chat-open?-)
                                         (let [props-session-id (:session-id props)]
                                           (or (s/blank? props-session-id)
                                               (= props-session-id @session-id-))))
                                    :ai-agent/chat-panel

                                    ;; An agent chat always fronts its own history,
                                    ;; conversation open or not.
                                    (or (contains? chat.d/general-agent-routes (first @cur-route-))
                                        (and (summary-route?) (not @history-collapsed?-)))
                                    :ai-agent/history-panel)]
                        (cond
                          (and panel (not= (first @active-panel-right-) panel))
                          (re/dispatch [:layout/show-panel-right [panel]])

                          (and (not panel) @active-panel-right-)
                          (re/dispatch [:layout/hide-panel-right]))))]
    (re/create-class
      {:component-did-mount
       (fn [this]
         ;; Init first — it seeds the header filter state the session load's date
         ;; params read. On the routes that resolve their agent by kind the session
         ;; load's agent comes from the agent list — when it hasn't loaded yet,
         ;; did-update retries once it has.
         (re/dispatch [:ai-agent/init])
         (when (and (kind-agent-ready?) (report-settings-ready?))
           (load-props-session! (re/props this)))
         (sync-panel! (re/props this)))
       :component-did-update (fn [this]
                               (when (and (kind-agent-ready?) (report-settings-ready?))
                                 (load-props-session! (re/props this)))
                               (sync-panel! (re/props this)))
       :component-will-unmount (fn [_]
                                 (re/dispatch [:layout/hide-panel-right]))
       :reagent-render
       (fn [args]
         @active-panel-right-
         @history-collapsed?-
         @single-pane?-
         @builder-chat-open?-
         ;; Deref'd so a session load re-renders (and re-runs sync-panel! via
         ;; did-update) even when the cond below short-circuits before reading it.
         @session-id-
         ;; Same for the agent list, which the cond below only reads after the concept
         ;; configuration — and that stays nil until a concept is picked. On the routes
         ;; resolving their agent by kind, the list IS what makes a session loadable
         ;; (`kind-agent-ready?`), so a link to one carrying no ?cid would otherwise sit
         ;; there with nothing fetching the list, the session, or the view it names.
         @agent-list-
         ;; And the report's settings, which `report-settings-ready?` gates the session
         ;; load on — this deref is what fetches them, and landing re-runs did-update.
         (when-let [report-id @builder-report-id-]
           @(re/subscribe [:reports/settings- report-id]))
         ;; A chat's answers are grounded in the concept/location it was asked under, so
         ;; once one is under way the header holds them — the New Chat button is the way
         ;; out. The builder is exempt: changing the view there re-renders its report.
         (let [locked? (and @session-started?-
                            (or (contains? chat.d/general-agent-routes (first @cur-route-))
                                (summary-route?)))]
           [l/logged-in-with-header-nav-vi (merge args
                                                  {:nav-container-style {:position :relative}
                                                   :scroll-container-style {:scrollbar-gutter (if (or @mobile?- (report-builder-route?))
                                                                                                :auto
                                                                                                "stable both-edges")}
                                                   :crew-type-radio? false
                                                   :timeframe-dropdown? (report-builder-route?)
                                                   :concept-dropdown-config {:disabled locked?}
                                                   :location-dropdown? (chat.d/location-scoped? (first @cur-route-))
                                                   :location-dropdown-config {:disabled locked?}
                                                   :agent-dropdown? (contains? chat.d/general-agent-routes
                                                                               (first @cur-route-))}
                                                  ;; The builder edits the report over the
                                                  ;; same view its readers get, so a filter
                                                  ;; it hides is hidden here too — its value
                                                  ;; is stripped from the re-render either
                                                  ;; way, and an inert control would only
                                                  ;; look like it still applied. Nil filters
                                                  ;; means the settings are still in flight,
                                                  ;; so nothing configurable shows yet.
                                                  (when @report-builder?-
                                                    (cond-> {:location-dropdown? (boolean (:location @builder-filters-))
                                                             :crew-type-radio? (boolean (:crew-type @builder-filters-))
                                                             :timeframe-dropdown? (boolean (:date @builder-filters-))}
                                                            @builder-target-concept-ids-
                                                            (assoc :concept-dropdown-config
                                                                   {:disabled locked?
                                                                    :filter-concept-ids @builder-target-concept-ids-}))))
            (cond
              (or (nil? @config-) (util/loading? @config-) (util/loading? @agent-list-))
              [:div {:style {:display :flex
                             :width "100%"
                             :min-height "100%"
                             :padding "10px 0"}}
               [empty-state/loading-card {:label "Loading Agent Interaction..."}]]

              ;; No agent picked and not viewing an existing session — prompt a selection.
              (and (empty? @selected-agent-)
                   (not @session-id-)
                   (s/blank? (:session-id args)))
              [:div {:style {:display :flex
                             :min-height "100%"
                             :width "100%"
                             :padding "12px 16px"}}
               [empty-state/no-agent-card]]

              @report-builder?-
              [:div {:style {:display :flex
                             :min-height "100%"
                             :width "100%"
                             :padding "12px 16px"}}
               [report-builder]]

              :else
              [:<>
               ;; Zero-height and sticky so it floats over the chat without pushing it down.
               (when (and (summary-route?) @history-collapsed?-)
                [:div {:style {:position :sticky
                               :top 0
                               :height 0
                               :z-index (inc (style/z :medium))}}
                 [:div {:style {:position :absolute
                                :top 12
                                :right 16
                                :display :flex
                                :gap 8}}
                  ;; The New Chat button otherwise lives in the hidden history panel.
                  (when (not-empty @url-session-id-)
                    [alc/tooltip
                     {:target-content [alc/up-button {:icon-type :AddIcon
                                                      :rounded? true
                                                      :on-click #(re/dispatch [:ai-agent/start-new-chat])}]
                      :tooltip-content "New Chat"}])
                  [alc/tooltip
                   {:target-content [alc/up-button {:icon-type :HistoryIcon
                                                    :variant "ghost"
                                                    :on-click #(re/dispatch [:ai-agent/toggle-history-collapsed])}]
                    :tooltip-content "Show Chat History"}]]])
               [chat.views/chat-content {}]])]))})))

(defn root-with-agent-context
  "`root` keyed by its agent context (chat.data/agent-context-key), so landing on a
   DIFFERENT agent remounts it — `:ai-agent/init` then drops the previous agent's
   conversation and the session in the URL is loaded fresh — while navigating within
   one agent's own routes (a first message adding a session id, the builder's save
   redirect) keeps it mounted and its state intact. Every agent route renders through
   this rather than `root` directly."
  [args]
  (let [[route-name _ query-params] @(re/subscribe [:router/current-route-])]
    ^{:key (chat.d/agent-context-key route-name query-params)}
    [root args]))
